import { useEffect, ChangeEvent, useState } from 'react';
import { useRequest } from 'ahooks';
import { FormikHelpers } from 'formik';

import {
  Form,
  Input,
  Dropdown,
  MaskedInput,
  CurrencyInput,
  Loading,
  Footer,
  ButtonWithArrowRight,
} from '~components';
import federationStates from '~constants/federationStates';
import { Federation, Address, Onboarding } from '~services';
import { formatCitiesOptions } from '~utils/formatters';
import { onlyNumbers } from '~constants/regularExpressions';
import { useLayout } from '~hooks';
import { useTransshipment } from '~contexts/TranshipmentContext';

import * as Styled from './styled';
import { formatRepresentative } from './formatters';
import { FormValues, Option } from './types';
import validationSchema from './validation';

function RepresentativePersonalDataCompletionForm() {
  const { setLayout } = useLayout();
  const [citiesOptions, setCitiesOptions] = useState([]);
  const [zipCodeError, setZipCodeError] = useState('');
  const [isAllAddressFieldsDisabled, setAllAddressFieldsDisabled] =
    useState(true);
  const [cellphoneValue, setCellphoneValue] = useState('');
  const [numberValue, setNumberValue] = useState('');

  const transshipment = useTransshipment();

  const { runAsync: fetchAddressByZipCode, loading: loadingAddressByZipCode } =
    useRequest(Address.getAddressByZipCode, {
      onError: () => setZipCodeError('CEP não encontrado.'),
      manual: true,
    });

  const {
    runAsync: fetchRepresentativeUpdate,
    loading: isLoadingFetchRepresentativeUpdate,
  } = useRequest(Onboarding.updateRepresentative, {
    manual: true,
  });

  const { run: fetchCitiesByStateId, loading: loadingCities } = useRequest(
    Federation.getCitiesByStateId,
    {
      onSuccess: (response) => {
        const formattedCities = formatCitiesOptions(response?.data);
        setCitiesOptions(formattedCities);
      },
      manual: true,
    }
  );

  const handleSelectState = (state: Option) => fetchCitiesByStateId(state.id);

  const initialValues: FormValues = {
    name: transshipment.data.name || '',
    email:
      transshipment.type === 'representative' ? transshipment.data.email : '',
    cellphone:
      transshipment.type === 'representative'
        ? transshipment.data.cellphone
        : '',
    cpf: '',
    birthdayDate: '',
    motherName: '',
    zipCode: '',
    state: '',
    city: '',
    neighborhood: '',
    street: '',
    number: '',
    complement: '',
    declaredIncome: '',
    politicallyExposedPerson: false,
  };

  const handleSubmit = async (values: FormValues) => {
    const formaterValues = formatRepresentative(values);

    await fetchRepresentativeUpdate(transshipment.id, formaterValues);

    transshipment.nextStep();
  };

  const handleChangeZipCode = async (
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikHelpers<FormValues>['setFieldValue']
  ) => {
    if (event.target.value.length === 9 && !loadingAddressByZipCode) {
      const zipCodeResponse = await fetchAddressByZipCode(
        event.target.value.replace(onlyNumbers, '')
      );

      setAllAddressFieldsDisabled(false);
      fetchCitiesByStateId(zipCodeResponse.data.state);

      setFieldValue('city', zipCodeResponse.data.city);
      setFieldValue('neighborhood', zipCodeResponse.data.neighborhood);
      setFieldValue('state', zipCodeResponse.data.state);
      setFieldValue('street', zipCodeResponse.data.street.slice(0, 40));
    } else {
      setZipCodeError('');
      setAllAddressFieldsDisabled(true);
    }
  };

  const handleChangeCellphoneMask = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
      .replace(/\D/g, '')
      .slice(0, 11)
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d)(\d{4})$/, '$1-$2');
    setCellphoneValue(newValue);
  };

  const handleChangeNumber = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value.replace(/D/, '').slice(0, 6);
    setNumberValue(newValue);
  };

  useEffect(() => {
    setLayout({
      header: {
        title: `Olá, ${transshipment.data.name}`,
        subtitle: 'Precisamos que você preencha alguns dados pessoais:',
      },
    });

    return () => setLayout({ header: {} });
  }, [transshipment.data.name]);

  return (
    <Form<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      aria-labelledby="form-register-representant-title"
      enableReinitialize
    >
      {({ isValid, setFieldValue }) => (
        <Styled.Column>
          <Input
            name="name"
            placeholder="Digite seu nome"
            label="Nome completo *"
            width="100%"
            type="text"
            maxLength={255}
          />
          <Input
            name="email"
            placeholder="Digite seu e-mail"
            label="E-mail *"
            width="100%"
            type="text"
            maxLength={50}
          />
          <Input
            name="cellphone"
            placeholder="Digite seu celular"
            label="Celular *"
            width="100%"
            value={cellphoneValue}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              handleChangeCellphoneMask(event)
            }
          />
          <MaskedInput
            mask="999.999.999-99"
            name="cpf"
            placeholder="Digite seu CPF"
            label="CPF *"
            width="100%"
          />
          <MaskedInput
            mask="99/99/9999"
            name="birthdayDate"
            placeholder="Digita a data do seu nascimento"
            label="Data de nascimento *"
            width="100%"
          />
          <Input
            name="motherName"
            placeholder="Nome completo da mãe"
            label="Nome completo da mãe *"
            width="100%"
            maxLength={255}
          />
          <MaskedInput
            mask="99999-999"
            name="zipCode"
            placeholder="Digite seu CEP"
            label="CEP *"
            width="100%"
            trailingIcon={loadingAddressByZipCode && <Loading />}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              handleChangeZipCode(event, setFieldValue)
            }
            externalError={zipCodeError}
          />
          <Dropdown
            name="state"
            label="Estado *"
            width="100%"
            placeholder="Selecione o estado"
            onSelect={handleSelectState}
            options={federationStates}
            disabled={isAllAddressFieldsDisabled}
          />
          <Dropdown
            name="city"
            label="Cidade *"
            placeholder="Selecione a cidade"
            trailingIcon={loadingCities && <Loading withBackground />}
            width="100%"
            options={citiesOptions}
            disabled={isAllAddressFieldsDisabled}
          />
          <Input
            name="neighborhood"
            placeholder="Digite o bairro"
            label="Bairro *"
            width="100%"
            disabled={isAllAddressFieldsDisabled}
            type="text"
            maxLength={40}
          />
          <Input
            name="street"
            placeholder="Digite o logradouro"
            label="Logradouro *"
            width="100%"
            disabled={isAllAddressFieldsDisabled}
            type="text"
            maxLength={40}
          />
          <Input
            name="number"
            type="number"
            placeholder="Digite o número"
            label="Número *"
            width="100%"
            disabled={isAllAddressFieldsDisabled}
            value={numberValue}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              handleChangeNumber(event)
            }
          />
          <Input
            name="complement"
            placeholder="Apartamento, sala, condomínio etc."
            label="Complemento"
            width="100%"
            disabled={isAllAddressFieldsDisabled}
            type="text"
            maxLength={30}
          />
          <CurrencyInput
            name="declaredIncome"
            placeholder="Renda declarada mensal"
            label="Renda declarada mensal *"
            width="100%"
            maxLength={15}
          />

          <Styled.Checkbox
            name="politicallyExposedPerson"
            label={
              <Styled.CheckboxLabel>
                Pessoa Exposta Politicamente (PEP)?
              </Styled.CheckboxLabel>
            }
          />
          <Footer
            marginTop="64px"
            havePaddingBottom
            rightButton={
              <ButtonWithArrowRight
                width="100%"
                variant="primary"
                disabled={!isValid || isLoadingFetchRepresentativeUpdate}
                loading={isLoadingFetchRepresentativeUpdate}
                type="submit"
              >
                Continuar
              </ButtonWithArrowRight>
            }
          />
        </Styled.Column>
      )}
    </Form>
  );
}

export default RepresentativePersonalDataCompletionForm;
